import { Command } from "cmdk";
import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import {
  Search, FileText, Database, Users, BookOpen, Truck, FileSignature, Settings, Activity, FileCheck, BarChart3, Package,
  ListChecks, Tag, ScrollText, Home,
} from "lucide-react";
import { endpoints } from "@/api/client";
import { debounce } from "@/lib/utils";

interface SearchResults {
  drafts: { id: string; doc_type: string; title: string | null; parent_id: string | null; status: string }[];
  contacts: { id: string; role: string; denominazione: string; rentri_iscrizione: string | null }[];
  rentri_cache: { id: string; resource_type: string; resource_key: string }[];
  audit: { id: string; action: string; resource_id: string | null; created_at: string }[];
}

/** Ordine allineato al menu laterale (operativo + avanzato). */
const PAGES = [
  { id: "/", label: "Home", icon: Home },
  { id: "/#primi-passi", label: "Primi passi", icon: ListChecks },
  { id: "/registri", label: "Registro", icon: Database },
  { id: "/giacenze", label: "Giacenze", icon: Package },
  { id: "/formulari", label: "Formulari", icon: FileText },
  { id: "/cer-attivi", label: "CER attivi", icon: Tag },
  { id: "/contacts", label: "Operatori", icon: Truck },
  { id: "/impostazioni", label: "Impostazioni", icon: Settings },
  { id: "/anagrafiche", label: "Anagrafiche", icon: Users },
  { id: "/codifiche", label: "Codifiche", icon: BookOpen },
  { id: "/vidimazione", label: "Vidimazione", icon: FileSignature },
  { id: "/drafts", label: "Bozze", icon: ScrollText },
  { id: "/jobs", label: "Job RENTRI", icon: FileCheck },
  { id: "/audit", label: "Audit log", icon: Activity },
  { id: "/analytics", label: "Analytics", icon: BarChart3 },
  { id: "/autista", label: "Mod. autista", icon: Truck },
];

export function CommandPalette({ open, onOpenChange }: { open: boolean; onOpenChange: (v: boolean) => void }) {
  const [query, setQuery] = useState("");
  const [results, setResults] = useState<SearchResults | null>(null);
  const navigate = useNavigate();

  useEffect(() => {
    if (!query || query.length < 2) {
      setResults(null);
      return;
    }
    const search = debounce(async () => {
      try {
        const r = await endpoints.search(query);
        setResults(r);
      } catch {
        setResults(null);
      }
    }, 250);
    search();
  }, [query]);

  const go = (path: string) => {
    onOpenChange(false);
    const hashIdx = path.indexOf("#");
    if (hashIdx >= 0) {
      const pathname = path.slice(0, hashIdx) || "/";
      const hash = path.slice(hashIdx);
      navigate({ pathname, hash });
      if (hash === "#primi-passi") {
        requestAnimationFrame(() =>
          document.getElementById("primi-passi")?.scrollIntoView({ behavior: "smooth", block: "start" }),
        );
      }
    } else {
      navigate(path);
    }
  };

  return (
    <Command.Dialog
      open={open}
      onOpenChange={onOpenChange}
      label="Ricerca globale"
      className="fixed inset-0 z-50 flex items-start justify-center bg-black/60 pt-[10vh]"
      overlayClassName="fixed inset-0 bg-black/60"
      contentClassName="w-full max-w-2xl"
    >
      <div className="w-full max-w-2xl rounded-xl border bg-popover text-popover-foreground shadow-2xl">
        <div className="flex items-center gap-2 border-b px-4 py-3">
          <Search className="h-4 w-4 text-muted-foreground" />
          <Command.Input
            value={query}
            onValueChange={setQuery}
            placeholder="Cerca operatori, registri, FIR, codici EER…"
            className="flex h-9 w-full bg-transparent text-sm outline-none placeholder:text-muted-foreground"
            autoFocus
          />
          <kbd className="hidden rounded border bg-muted px-1.5 text-[10px] font-medium text-muted-foreground sm:inline-block">ESC</kbd>
        </div>
        <Command.List className="max-h-[60vh] overflow-y-auto p-2">
          <Command.Empty className="py-6 text-center text-sm text-muted-foreground">Nessun risultato.</Command.Empty>
          <Command.Group heading="Pagine">
            {PAGES.map((p) => {
              const Icon = p.icon;
              return (
                <Command.Item key={p.id} value={p.label} onSelect={() => go(p.id)}
                  className="flex cursor-pointer items-center gap-3 rounded-md px-3 py-2 text-sm aria-selected:bg-accent aria-selected:text-accent-foreground">
                  <Icon className="h-4 w-4 text-muted-foreground" />
                  <span>{p.label}</span>
                </Command.Item>
              );
            })}
          </Command.Group>
          {results && results.drafts.length > 0 && (
            <Command.Group heading="Bozze">
              {results.drafts.map((d) => (
                <Command.Item key={d.id} value={`draft-${d.id}`} onSelect={() => go(`/drafts/${d.id}`)}
                  className="flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm aria-selected:bg-accent">
                  <div className="flex items-center gap-3"><FileText className="h-4 w-4 text-muted-foreground" />
                    <span>{d.title ?? d.id.slice(0, 8)}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{d.doc_type} · {d.status}</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}
          {results && results.contacts.length > 0 && (
            <Command.Group heading="Operatori">
              {results.contacts.map((c) => (
                <Command.Item key={c.id} value={`c-${c.id}`} onSelect={() => go(`/contacts`)}
                  className="flex cursor-pointer items-center justify-between rounded-md px-3 py-2 text-sm aria-selected:bg-accent">
                  <div className="flex items-center gap-3"><Truck className="h-4 w-4 text-muted-foreground" />
                    <span>{c.denominazione}</span>
                  </div>
                  <span className="text-xs text-muted-foreground">{c.role}</span>
                </Command.Item>
              ))}
            </Command.Group>
          )}
        </Command.List>
        <div className="border-t px-4 py-2 text-[11px] text-muted-foreground">
          Suggerimento: <kbd className="rounded border bg-muted px-1">↑↓</kbd> naviga, <kbd className="rounded border bg-muted px-1">Enter</kbd> seleziona
        </div>
      </div>
    </Command.Dialog>
  );
}
